import { useEffect, useRef, useState } from "react";
import { api, ExportFmt } from "@/api/client";
import { useAuthStore } from "@/stores/auth";

/** Button with dropdown: Export as [csv|json|md|txt].
 *  Works for a single conversation (pass `convId`) or all ({undefined}).
 *  We fetch the protected endpoint via authenticated XHR, then trigger a
 *  browser download from the in-memory blob — <a href> with Bearer tokens
 *  isn't trivially supported. */
export function ExportMenu({
  convId,
  label = "Export",
  className = "",
}: {
  convId?: number;
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<ExportFmt | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  async function download(fmt: ExportFmt) {
    setBusy(fmt);
    setErr(null);
    try {
      const token = useAuthStore.getState().token;
      const url = api.adminExportUrl(fmt, convId);
      const resp = await fetch(url, {
        headers: {
          Accept: "application/octet-stream",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!resp.ok) {
        const body = await resp.text();
        throw new Error(`${resp.status}: ${body.slice(0, 200)}`);
      }
      const disp = resp.headers.get("content-disposition") || "";
      const match = disp.match(/filename="([^"]+)"/i);
      const filename =
        match?.[1] ??
        (convId
          ? `fintbot-conv${convId}.${fmt}`
          : `fintbot-all.${fmt}`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      setOpen(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div ref={menuRef} className={`relative inline-block ${className}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="rounded-md border border-bg-border bg-bg-elevated px-3 py-1.5 text-xs font-medium text-white hover:bg-bg-hover"
      >
        {label} ▾
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-40 rounded-md border border-bg-border bg-bg-card shadow-card">
          {(["md", "txt", "csv", "json"] as ExportFmt[]).map((f) => (
            <button
              key={f}
              disabled={busy !== null}
              onClick={() => download(f)}
              className="flex w-full items-center justify-between px-3 py-1.5 text-left text-xs hover:bg-bg-hover disabled:opacity-50"
            >
              <span className="uppercase">{f}</span>
              {busy === f ? (
                <span className="text-ink-dim">…</span>
              ) : (
                <span className="text-ink-dim">↓</span>
              )}
            </button>
          ))}
          {err && (
            <div className="border-t border-bg-border px-3 py-1.5 text-[11px] text-red-400">
              {err}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
